import Diagram, { type RenderToWhiteboardOptions } from '../../common/Diagram';
import { type WhiteboardData, type LayoutOptions } from '../../types';
import {
  randomId,
  arcArrowPath,
  normalizeSvgPath,
  computeIntersectionAngles,
  type ArcArrowStyles,
} from '../../common/utils';
import { whiteboardObjectForNode } from '../../common/whiteboardUtils';
import properties from './properties';
import description from './description';

class CycleDiagram extends Diagram {
  static diagramName = 'Cycle';
  static diagramType = 'cycle';

  arrows: any[] = [];

  static description = description;
  static diagramProperties = properties;
  static allowedProperties = {
    ...Diagram.allowedProperties,
    ...this.diagramProperties,
  };

  detect(): boolean {
    throw new Error('Method not implemented.');
  }

  async layout(layoutOptions: LayoutOptions) {
    // Nodes are all the same size in a cycle diagram.
    const diagramNodeWidth = this.getDiagramProperty('nodeWidth') as number;
    const diagramNodeHeight = this.getDiagramProperty('nodeHeight') as number;

    for (const node of this.database.nodes()) {
      await this.sizeNode(node, layoutOptions);
    }

    const fromAngle = 0;
    const toAngle = 2 * Math.PI; // 1.5 * Math.PI;
    const numNodes = this.database.nodeIds.length;
    const angleStep = (toAngle - fromAngle) / numNodes;

    // Determine the radius of the circle.
    let radius = (diagramNodeWidth * 1.5) / Math.sin(angleStep);

    // Special cases for 0, 1, 2 nodes.
    if (numNodes === 1) {
      radius = 0;
    } else if (numNodes === 2) {
      radius = diagramNodeWidth;
    } else if (numNodes === 3) {
      radius = diagramNodeWidth * 1.5;
    }

    console.log(`radius: ${radius}`);

    let angle = fromAngle;
    for (const node of this.database.nodes()) {
      node.layout.left =
        radius * Math.cos(angle - Math.PI / 2) - node.layout.width / 2; // - Math.PI / 2 to start at the top.
      node.layout.top =
        radius * Math.sin(angle - Math.PI / 2) - node.layout.height / 2;
      angle += angleStep;
    }

    // The dimensions of the graph are the extent of the nodes
    // on the circle.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const node of this.database.nodes()) {
      minX = Math.min(minX, node.layout.left);
      minY = Math.min(minY, node.layout.top);
      maxX = Math.max(maxX, node.layout.left + node.layout.width);
      maxY = Math.max(maxY, node.layout.top + node.layout.height);
    }

    const width = maxX - minX;
    const height = maxY - minY;
    this.database.layout.left = minX;
    this.database.layout.top = minY;
    this.database.layout.width = width;
    this.database.layout.height = height;

    // Offset nodes so they are centered around the bounding box.
    for (const node of this.database.nodes()) {
      node.layout.left += width / 2;
      node.layout.top += height / 2;
    }

    // Compute the intersection between the rectangles and the circle. Yes
    // the width and height are backwards here. I can't work out why.
    angle = fromAngle;
    let [startAngle, lastAngle] = computeIntersectionAngles(
      0,
      0,
      radius,
      diagramNodeHeight,
      diagramNodeWidth,
      angle
    ).sort((a, b) => a - b);
    console.log(`[${lastAngle},${startAngle}]`);

    for (let i = 0; i < numNodes - 1; i++) {
      angle += angleStep;

      const [endAngle, nextStartAngle] = computeIntersectionAngles(
        0,
        0,
        radius,
        diagramNodeHeight,
        diagramNodeWidth,
        angle
      ).sort((a, b) => a - b);
      console.log(`[${endAngle},${nextStartAngle}]`);

      const offsetAngle = (endAngle - startAngle) / 10;
      this.arrows.push({
        radius: radius,
        startAngle: startAngle + offsetAngle,
        endAngle: endAngle - offsetAngle,
      });
      startAngle = nextStartAngle;
    }
    if (numNodes > 1) {
      const offsetAngle = (lastAngle - startAngle) / 10;
      this.arrows.push({
        radius: radius,
        startAngle: startAngle + offsetAngle,
        endAngle: lastAngle - offsetAngle,
      });
    }
  }

  renderToWhiteboard(options?: RenderToWhiteboardOptions): any {
    const { nodeAttrs } = options || {};
    const whiteboard: WhiteboardData = {
      version: '1.2.6',
      objects: {},
    };
    const colorScheme = this.getColorScheme();

    const arrowStyle = this.getDiagramProperty('arrowStyle') as string;
    const arrowLocation = this.getDiagramProperty('arrowLocation') as string;
    const arrowThickness = this.getDiagramProperty('arrowThickness') as number;
    const diagramNodeWidth = this.getDiagramProperty('nodeWidth') as number;

    /* Arrow behind diagram. */
    if (arrowLocation === 'behind') {
      const arrowPath = arcArrowPath(
        0,
        0,
        (30 * Math.PI) / 180 - Math.PI / 2, // Minus PI/2 to start at the top.
        (330 * Math.PI) / 180 - Math.PI / 2,
        this.width() / 2 - diagramNodeWidth / 2,
        arrowThickness,
        arrowStyle as ArcArrowStyles
      );
      const arrowId = randomId();
      const connectorColor = colorScheme.connectorColor({ n: 0, total: 1 });
      whiteboard.objects[arrowId] = {
        type: 'shape',
        shapeName: 'customPath',
        id: arrowId,
        left: 0,
        top: 0,
        width: this.width(),
        height: this.height(),
        scaleX: 1,
        scaleY: 1,
        shapePath: arrowPath,
        fill: connectorColor.fill,
        stroke: connectorColor.stroke,
        strokeWidth: connectorColor.strokeWidth,
      };
    }

    for (let i = 0; i < this.arrows.length; i++) {
      const arrow = this.arrows[i];
      if (arrowLocation === 'between') {
        const arrowPathData = arcArrowPath(
          0,
          0,
          arrow.startAngle - Math.PI / 2, // Minus PI/2 to start at the top.
          arrow.endAngle - Math.PI / 2,
          arrow.radius,
          arrowThickness,
          arrowStyle as ArcArrowStyles
        );

        const {
          path: arrowPath,
          left: arrowLeft,
          top: arrowTop,
        } = normalizeSvgPath(arrowPathData);

        // TODO:
        /*
      // Compute the bounding box of the path and adjust the object appropriately.
      const bounds = fabric.Path.prototype._calcDimensions.call({
        path: fabric.util.makePathSimpler(fabric.util.parsePath(arrowPath)),
      });
      */

        const arrowId = randomId();
        const connectorColor = colorScheme.connectorColor({
          n: i,
          total: this.arrows.length,
        });
        whiteboard.objects[arrowId] = {
          type: 'shape',
          shapeName: 'customPath',
          id: arrowId,
          left: arrowLeft + this.width() / 2,
          top: arrowTop + this.height() / 2,
          width: arrowPath.bounds.width,
          height: arrowPath.bounds.height,
          scaleX: 1,
          scaleY: 1,
          shapePath: arrowPath.pathData,
          fill: connectorColor.fill,
          stroke: connectorColor.stroke,
          strokeWidth: connectorColor.strokeWidth,
        };
      }
    }

    for (let i = 0; i < this.database.nodeIds.length; i++) {
      const node = this.database.node(this.database.nodeIds[i]);
      whiteboardObjectForNode(
        whiteboard,
        this,
        node,
        {
          n: i,
          total: this.database.nodeIds.length,
        },
        nodeAttrs?.(node)
      );
    }

    return whiteboard;
  }
}

export default CycleDiagram;
